import { getFormattedDate } from '@finn/platform-modules';
import { ModalKey, useCloseModal } from '@finn/ua-modals';
import { interactionTrackingEvent, TrackingEventName } from '@finn/ua-tracking';
import {
  CookieKeys,
  setClientCookie,
  traceabilityHeadersBrowser,
  trackEvent,
  useUniversalRouter,
} from '@finn/ui-utils';
import axios from 'axios';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import { LoginScreen, useLoginStore } from '../store/useLoginStore';

export const useCompleteRegistrationHandler = ({
  createLead,
  setExtendedAccountInProgress,
}: {
  createLead?: (data) => Promise<void>;
  setExtendedAccountInProgress?: (value: boolean) => void;
}) => {
  const form = useFormContext();
  const setServerErrorCode = useLoginStore((state) => state.setServerErrorCode);
  const setFormValues = useLoginStore((state) => state.setFormValues);
  const setLoginScreen = useLoginStore((state) => state.setLoginScreen);
  const setIsRegistrationIncomplete = useLoginStore(
    (state) => state.setIsRegistrationIncomplete
  );
  const trackSubmitClick = useCallback((data) => {
    interactionTrackingEvent(TrackingEventName.CTA_CLICKED, {
      location: 'registration-completion',
      ...(data || {}),
    });
  }, []);
  const trackFailure = useCallback((error = '', data) => {
    interactionTrackingEvent(
      TrackingEventName.REGISTRATION_DATA_UPDATE_FAILED,
      {
        location: 'registration-completion',
        error,
        ...(data || {}),
      }
    );
  }, []);
  const router = useUniversalRouter();
  const isCart = router?.asPath.includes('cart');
  const setSubmittedLogin = useLoginStore((state) => state.setSubmittedLogin);
  const closeModal = useCloseModal();

  return useCallback(async () => {
    const formValues = form?.getValues() || {};
    setFormValues(formValues);

    const {
      email,
      firstname = '',
      lastname = '',
      phone = '',
      birthday = '',
      emailSubscription = false,
    } = formValues;

    const shouldCreateLead = !!(
      firstname &&
      lastname &&
      phone &&
      birthday &&
      isCart
    );

    setServerErrorCode(null);

    trackSubmitClick({ source: 'registration-completion' });
    if (shouldCreateLead) {
      setExtendedAccountInProgress?.(true);
    }
    setSubmittedLogin(true);

    try {
      await axios.patch(
        `/api/checkout/v2/contact`,
        {
          firstname,
          lastname,
          phone,
          birthday: getFormattedDate(birthday, 'DD.MM.YYYY'),
          emailSubscription,
        },
        {
          headers: traceabilityHeadersBrowser(),
        }
      );

      closeModal(ModalKey.LOGIN);
      trackEvent('Registration Data Completed', {
        contact_id: '',
        origin: 'checkout',
      });
      if (shouldCreateLead) {
        try {
          await createLead?.(formValues);
        } catch {
          setSubmittedLogin(false);
          setExtendedAccountInProgress?.(false);
        }
      }
      setIsRegistrationIncomplete(false, '');
      setLoginScreen(LoginScreen.loginForm);
      setClientCookie(CookieKeys.REGISTRATION_DATA_SUBMITTED, email);
    } catch {
      trackFailure('ERROR', { source: 'registration-completion' });
      setServerErrorCode('Registration data update failed');
    }
    if (!shouldCreateLead) {
      setSubmittedLogin(false);
    }
  }, [
    setFormValues,
    form,
    isCart,
    setServerErrorCode,
    trackSubmitClick,
    setSubmittedLogin,
    setExtendedAccountInProgress,
    closeModal,
    createLead,
    setIsRegistrationIncomplete,
    setLoginScreen,
    trackFailure,
  ]);
};
